import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../usercomponents/SideBar';
import TopNavBar from '../usercomponents/TopNavBar';
import Footer from '../usercomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function formatDateTime(v) {
  if (!v) return '—';
  return new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function QuestionResultCard({ q }) {
  const options = q.options || [];
  const correctIds = q.correct_answer?.correct_option_ids || [];
  const answer = q.student_answer;

  return (
    <div className="card shadow-sm border-0 mb-16">
      <div className="card-body p-20">
        <div className="d-flex justify-content-between mb-8">
          <span className="text-13 text-gray-400">Q{q.display_order} · {q.question_type.replace('_', ' ')}</span>
          <span className="fw-semibold">{q.marks_awarded !== null && q.marks_awarded !== undefined ? `${q.marks_awarded} / ${q.max_marks}` : `Pending / ${q.max_marks}`}</span>
        </div>
        <div className="fw-semibold mb-12">{q.question_text}</div>

        {['single_choice', 'multiple_choice', 'true_false'].includes(q.question_type) && (
          <div className="d-flex flex-column gap-6">
            {options.map((o) => {
              const isCorrect = correctIds.includes(o.id);
              const isSelected = q.question_type === 'multiple_choice' ? (answer?.selected_option_ids || []).includes(o.id) : answer?.selected_option_id === o.id;
              return (
                <div key={o.id} className="d-flex align-items-center gap-8 p-8" style={{
                  borderRadius: 8, background: isCorrect ? '#e9f9ee' : isSelected ? '#fdeeee' : 'transparent',
                }}>
                  <span className={isCorrect ? 'text-success fw-semibold' : ''}>{o.text}</span>
                  {isSelected && <span className="text-13 text-gray-500">(your answer)</span>}
                  {isCorrect && <span className="badge bg-success-subtle text-success-emphasis">Correct</span>}
                </div>
              );
            })}
          </div>
        )}

        {['short_text', 'fill_blank'].includes(q.question_type) && (
          <div>
            <div>Your answer: <strong>{answer?.text || <span className="text-gray-400">(unanswered)</span>}</strong></div>
            {q.correct_answer && <div className="text-13 text-gray-500">Accepted: {(q.correct_answer.accepted_answers || []).join(', ')}</div>}
          </div>
        )}

        {q.question_type === 'numerical' && (
          <div>
            <div>Your answer: <strong>{answer?.value ?? <span className="text-gray-400">(unanswered)</span>}</strong></div>
            {q.correct_answer && <div className="text-13 text-gray-500">Correct: {q.correct_answer.value} ± {q.correct_answer.tolerance}</div>}
          </div>
        )}

        {q.question_type === 'long_answer' && (
          <div className="p-12" style={{ background: '#f6faf8', borderRadius: 8 }}>
            {answer?.text || <span className="text-gray-400">(unanswered)</span>}
          </div>
        )}

        <div className="mt-8">
          {q.is_correct === true && <span className="text-success text-13"><i className="ph ph-check-circle me-1" />Correct</span>}
          {q.is_correct === false && <span className="text-danger text-13"><i className="ph ph-x-circle me-1" />Incorrect</span>}
          {q.evaluation_status === 'pending_evaluation' && <span className="text-warning text-13"><i className="ph ph-clock me-1" />Pending Evaluation</span>}
        </div>
        {q.explanation && <div className="text-13 text-gray-500 mt-8"><strong>Explanation:</strong> {q.explanation}</div>}
        {q.evaluator_comment && <div className="text-13 text-gray-500 mt-4"><strong>Feedback:</strong> {q.evaluator_comment}</div>}
      </div>
    </div>
  );
}

function MockExamResult() {
  const { attemptId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getToken = () => JSON.parse(sessionStorage.getItem('studentSession'))?.token;

  const fetchResult = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/student/mock-exams/attempts/${attemptId}/result`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setData(response.data);
      else setError(response.data.message || 'Failed to load result.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading result.');
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  useEffect(() => { fetchResult(); }, [fetchResult]);

  if (loading) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper"><TopNavBar /><div className="dashboard-body text-center py-40"><div className="spinner-border text-main-600" /></div><Footer /></div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <SideBar />
        <div className="dashboard-main-wrapper"><TopNavBar /><div className="dashboard-body"><div className="alert alert-danger">{error}</div></div><Footer /></div>
      </>
    );
  }

  const { exam, attempt, questions, showCorrectAnswers } = data;
  const isPending = attempt.status === 'pending_evaluation';

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body px-3">
          <div className="breadcrumb mb-24">
            <ul className="d-flex align-items-center gap-2 list-unstyled">
              <li><Link to="/mock-exams" className="text-gray-200 text-15 hover-text-main-600 text-decoration-none">Mock Exams</Link></li>
              <li><span className="text-gray-500 d-flex align-items-center"><i className="ph ph-caret-right" /></span></li>
              <li><span className="text-main-600 text-15">Result</span></li>
            </ul>
          </div>

          <div className="card shadow-sm border-0 mb-24">
            <div className="card-body p-24">
              <h4 className="fw-bold mb-4">{exam.title}</h4>
              <div className="text-gray-500 mb-20">{exam.coursename} · Attempt #{attempt.attempt_number}</div>

              {isPending ? (
                <div className="alert alert-warning">
                  This attempt has descriptive answers pending manual evaluation. Your final result will be available once your instructor evaluates them.
                </div>
              ) : (
                <div className="d-flex align-items-center gap-24 mb-20">
                  <div className={`badge text-16 py-8 px-16 ${attempt.pass_fail === 'pass' ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'}`}>
                    {attempt.pass_fail === 'pass' ? 'PASSED' : 'FAILED'}
                  </div>
                  <div className="text-24 fw-bold">{attempt.total_marks_scored} / {attempt.total_marks_possible}</div>
                  <div className="text-16 text-gray-500">{attempt.percentage}%</div>
                </div>
              )}

              <div className="row g-16">
                <div className="col-sm-3 col-6"><div className="text-13 text-gray-400">Total Questions</div><div className="fw-semibold">{attempt.total_questions}</div></div>
                <div className="col-sm-3 col-6"><div className="text-13 text-gray-400">Answered</div><div className="fw-semibold">{attempt.answered_questions}</div></div>
                <div className="col-sm-3 col-6"><div className="text-13 text-gray-400">Correct</div><div className="fw-semibold text-success">{attempt.correct_answers}</div></div>
                <div className="col-sm-3 col-6"><div className="text-13 text-gray-400">Incorrect</div><div className="fw-semibold text-danger">{attempt.incorrect_answers}</div></div>
                <div className="col-sm-3 col-6"><div className="text-13 text-gray-400">Unanswered</div><div className="fw-semibold">{attempt.unanswered_questions}</div></div>
                <div className="col-sm-3 col-6"><div className="text-13 text-gray-400">Started</div><div>{formatDateTime(attempt.started_at)}</div></div>
                <div className="col-sm-3 col-6"><div className="text-13 text-gray-400">Submitted</div><div>{formatDateTime(attempt.submitted_at)}</div></div>
                <div className="col-sm-3 col-6"><div className="text-13 text-gray-400">Submission Type</div><div className="text-capitalize">{attempt.submission_type}</div></div>
              </div>
            </div>
          </div>

          {!showCorrectAnswers && (
            <div className="alert alert-info mb-16">Correct answers will be shown according to your instructor's settings for this exam.</div>
          )}

          {questions.map((q) => (<QuestionResultCard key={q.id} q={q} />))}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default MockExamResult;
